import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown, Trash2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface RangeFilterProps {
  filter: {
    label: string;
    value: {
      min: number | null;
      max: number | null;
    };
    onChange: (range: { min: number | null; max: number | null }) => void;
  };
}

export const RangeFilter = ({ filter }: RangeFilterProps) => {
  const { t } = useLanguage();
  const hasFilter = filter.value.min !== null || filter.value.max !== null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[200px] justify-between rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-normal text-gray-700",
            "flex items-center gap-4 hover:bg-gray-50/50 hover:text-gray-900 hover:border-gray-300",
            "focus:outline-none focus:ring-2 focus:ring-gray-200"
          )}
        >
          <span className="truncate flex-1 text-left">{filter.label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] rounded-lg border border-gray-200 bg-white p-0">
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("dashboard.products.filters.min")}</label>
            <Input
              type="number"
              value={filter.value.min ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? null : Number(e.target.value);
                filter.onChange({ ...filter.value, min: value });
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("dashboard.products.filters.max")}</label>
            <Input
              type="number"
              value={filter.value.max ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? null : Number(e.target.value);
                filter.onChange({ ...filter.value, max: value });
              }}
            />
          </div>
        </div>
        {hasFilter && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-gray-500 hover:text-gray-900"
                onClick={() => filter.onChange({ min: null, max: null })}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t("dashboard.products.filters.clear")}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
